import { useMutation, useLazyQuery } from '@apollo/client'
import { useTheme } from '@material-ui/core'
import { Formik, Form, FormikHelpers } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import apolloClient from '../../graphql/apolloClient'
import { UPDATE_SITEKEY } from '../../graphql/sitekeys'
import { GET_SITEKEY_SINGLE_ONE } from '../../graphql/sitekeys'
import {
  AssociatedGateway,
  BookingRedirect,
  SiteKeys,
  SiteKeyUpload
} from '../../types/sitekey'
import SitekeyBasicInfo from './SitekeyBasicInfo'
import SitekeyFooter from './SitekeyFooter'
import SitekeyLogoUpload from './SitekeyLogoUpload'
import SitekeyWBSDetails from './SitekeyWBSDetails'
import { buildSiteKeyUpdatePayload } from './siteKeyUpdatePayload'
import { SiteKeyItemsValidationSchema } from './validations/SiteKeyItems'

// get the sitekey single one data
const SITEKEY_FORM_INIT_VALUES: SiteKeyUpload = {
  EntityType: '',
  EntityId: '',
  UserId: '',
  Email: '',
  AssociatedGateways: [],
  Type: 'Widget',
  CORSURLs: [],
  Hosts: [],
  Logo: {
    EN: undefined,
    FA: undefined
  },
  Favicon: {
    EN: undefined,
    FA: undefined
  },
  BookingRedirects: [],
  EntityCode: '',
  DisplayEntityCode: '',
  WalletType: '',
  Mode: ''
}

interface SitekeyMainFormProps {
  autoCompleteLabelForEntity: any
  setautoCompleteLabelForEntity: React.Dispatch<any>
}

const SitekeyMainForm: React.FC<SitekeyMainFormProps> = ({
  autoCompleteLabelForEntity,
  setautoCompleteLabelForEntity
}) => {
  const params = useParams<{ id: string }>()
  const theme = useTheme()

  const [initialValues, setinitialValues] = useState<SiteKeyUpload>(
    SITEKEY_FORM_INIT_VALUES
  )
  const [areAllLogosUploaded, setAreAllLogosUploaded] = useState<boolean>(false)
  const [bookingRedirectsState, setBookingRedirectsState] = useState<
    BookingRedirect[]
  >([])
  const [associatedGateways, setAssociatedGateways] = useState<
    AssociatedGateway[]
  >([])

  const [getInitialValues, { loading, refetch: refetchForSiteKey }] =
    useLazyQuery<{
      sitekey: SiteKeys
    }>(GET_SITEKEY_SINGLE_ONE, {
      onCompleted: (data) => {
        setinitialValues({
          EntityType: data.sitekey.EntityType,
          EntityId: data.sitekey.EntityId,
          UserId: data.sitekey.UserId,
          Email: data.sitekey.Email,
          AssociatedGateways: data.sitekey.AssociatedGateways,
          Type: data.sitekey.Type,
          CORSURLs: data.sitekey.CORSURLs,
          Hosts: data.sitekey.Hosts,
          SiteKey: data.sitekey.SiteKey,
          Logo: data.sitekey.Logo,
          Favicon: data.sitekey.Favicon,
          BookingRedirects: data.sitekey.BookingRedirects,
          EntityCode: data.sitekey.EntityCode,
          DisplayEntityCode: data.sitekey.DisplayEntityCode,
          WalletType: data.sitekey.WalletType,
          Mode: data.sitekey.Mode
        })
      },
      onError: (err) => {
        toast.error(err.message)
      }
    })

  const formikValuesRef = useRef(null)

  useEffect(() => {
    if (params.id) {
      getInitialValues({
        variables: {
          sitekeyID: params.id
        }
      })
    }
  }, [])

  const [mutateFunctionForUpdate, { loading: loadingForUpdateSitekey }] =
    useMutation(UPDATE_SITEKEY, {
      notifyOnNetworkStatusChange: true,
      onCompleted: () => {
        refetchForSiteKey()
      }
    })

  const onSave = (
    values: SiteKeyUpload,
    { setSubmitting }: FormikHelpers<SiteKeyUpload>
  ) => {
    const payload = buildSiteKeyUpdatePayload(
      values,
      bookingRedirectsState,
      associatedGateways
    )

    mutateFunctionForUpdate({
      variables: {
        input: payload,
        id: params.id
      }
    })
      .then(({ data }) => {
        toast.success(data.updateSiteKey.message)
      })
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : 'ERROR!')
      })
      .finally(() => {
        setSubmitting(false)
      })

    apolloClient.cache.evict({ fieldName: 'sitekeys' })
  }

  return (
    <Formik
      innerRef={formikValuesRef}
      initialValues={initialValues}
      onSubmit={onSave}
      enableReinitialize
      validationSchema={SiteKeyItemsValidationSchema}>
      {({
        isSubmitting,
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isValid
      }) => {
        if (
          values.Logo.EN?.fileURL &&
          values.Logo.FA?.fileURL &&
          values.Favicon.EN?.fileURL &&
          values.Favicon.FA?.fileURL
        ) {
          setAreAllLogosUploaded(true)
        }

        return (
          <>
            <Form>
              <div>
                <SitekeyBasicInfo
                  autoCompleteLabelForEntity={autoCompleteLabelForEntity}
                  setautoCompleteLabelForEntity={setautoCompleteLabelForEntity}
                  bookingRedirectsState={bookingRedirectsState}
                  setBookingRedirectsState={setBookingRedirectsState}
                  {...{
                    values,
                    setFieldValue,
                    errors,
                    touched,
                    handleBlur,
                    isSubmitting
                  }}
                />
                <SitekeyWBSDetails
                  {...{
                    values,
                    setFieldValue,
                    errors,
                    touched,
                    handleBlur,
                    isSubmitting
                  }}
                  associatedGateways={associatedGateways}
                  setAssociatedGateways={setAssociatedGateways}
                />
                <SitekeyLogoUpload
                  {...{
                    values,
                    setFieldValue,
                    sitekeyId: params.id
                  }}
                />
                <SitekeyFooter
                  areAllLogosUploaded={areAllLogosUploaded}
                  {...{
                    isValid,
                    isSubmitting,
                    loadingForUpdateSitekey
                  }}
                />
              </div>
            </Form>
            <AdotelSkeleton
              open={loading}
              circlesColor={theme.palette.primary.main}
              numberOfCircles={4}
            />
          </>
        )
      }}
    </Formik>
  )
}
export default SitekeyMainForm
